import React from "react";
import { Card, Space, Typography, Row, Col, Divider } from "antd";
import { EnvironmentOutlined, TagOutlined } from "@ant-design/icons";
import { LocationType } from "../../modal/location-types";

const { Text } = Typography;

interface LocationColorLegendProps {
  visible?: boolean;
  locations?: LocationType[];
}

const LocationColorLegend: React.FC<LocationColorLegendProps> = ({
  visible = true,
  locations,
}) => {
  if (!visible) return null;

  // Category colors for shift types
  const categoryColors = [
    { key: "scheduled", label: "Regular Shift", color: "#1890ff" },
    { key: "timeoff", label: "Time Off Approved", color: "#52c41a" },
    { key: "timeoffpending", label: "Time Off Pending", color: "#faad14" },
  ];

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
      title={
        <Space>
          <EnvironmentOutlined style={{ color: "#1890ff" }} />
          <Text strong>Color Legend</Text>
        </Space>
      }
    >
      <Row gutter={[24, 16]}>
        {/* Locations/Branches */}
        <Col xs={24} md={12}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <EnvironmentOutlined style={{ color: "#1890ff", fontSize: 16 }} />
              <Text strong style={{ fontSize: 14 }}>
                Branches (Shift Colors)
              </Text>
            </div>

            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {locations && locations.length > 0 ? (
                locations.map((location) => (
                  <div
                    key={location.key}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        backgroundColor: location.color,
                        border: "1px solid #d9d9d9",
                        flexShrink: 0,
                      }}
                    />
                    <Text style={{ fontSize: 13 }}>{location.name}</Text>
                  </div>
                ))
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No branches available
                </Text>
              )}
            </Space>
          </Space>
        </Col>

        {/* Categories */}
        <Col xs={24} md={12}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TagOutlined style={{ color: "#52c41a", fontSize: 16 }} />
              <Text strong style={{ fontSize: 14 }}>
                Shift Types
              </Text>
            </div>

            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {categoryColors.map((category) => (
                <div
                  key={category.key}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: category.color,
                      border: "1px solid #d9d9d9",
                      flexShrink: 0,
                    }}
                  />
                  <Text style={{ fontSize: 13 }}>{category.label}</Text>
                </div>
              ))}
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider style={{ margin: "16px 0 12px 0" }} />

      {/* Color Usage Explanation */}
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Text strong style={{ fontSize: 13, color: "#595959" }}>
          How Colors Work:
        </Text>
        <Space direction="vertical" size={4}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 20,
                height: 12,
                backgroundColor: "#1890ff",
                borderRadius: 4,
              }}
            />
            <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
              <strong>Shift Block Color</strong> = Branch/Location assigned to
              shift
            </Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 20,
                height: 12,
                backgroundColor: "#52c41a",
                borderRadius: 4,
              }}
            />
            <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
              <strong>Green</strong> = Approved time off
            </Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 20,
                height: 12,
                backgroundColor: "#faad14",
                borderRadius: 4,
              }}
            />
            <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
              <strong>Orange</strong> = Pending time off
            </Text>
          </div>
        </Space>
      </Space>
    </Card>
  );
};

export default LocationColorLegend;
